// PBR "More to Explore" — lazy related-products fetch.
//
// Shopify only computes product recommendations through the Section
// Rendering API, so the section renders placeholders (editor) or an empty
// rail on first paint and this element swaps in the real cards once the
// section scrolls near the viewport. The inner pbr-collection-slider custom
// element re-initialises automatically when the new markup connects.

class PbrMoreToExplore extends HTMLElement {
  connectedCallback() {
    const url = this.dataset.url;
    if (!url || this.dataset.loaded) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.observer.disconnect();
          this.load(url);
        }
      },
      // Generous margin: the fetch + swap should finish while the band is
      // still well below the fold, so shoppers only ever see the real cards.
      { rootMargin: '1400px 0px' }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  async load(url) {
    try {
      const response = await fetch(url, { headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const text = await response.text();
      // Same-origin Section Rendering API response — the same Liquid this
      // theme serves. Adopted as parsed nodes, not an HTML string sink.
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const fresh = doc.querySelector('pbr-more-to-explore');

      const freshItems = fresh?.querySelectorAll('.pbr-collection-slider__item:not([data-pbr-placeholder])');
      if (!fresh || !freshItems?.length) {
        // No recommendations for this product — drop the whole band.
        this.hidden = true;
        return;
      }

      this.dataset.loaded = 'true';
      this.replaceChildren(...fresh.childNodes);
      document.dispatchEvent(new CustomEvent('pbr:content:updated'));
    } catch (error) {
      // Keep whatever is on screen (placeholders in the editor, empty rail
      // otherwise) rather than blanking the page over a failed fetch.
      console.warn('[pbr-more-to-explore] fetch failed:', error);
    }
  }
}

if (!customElements.get('pbr-more-to-explore')) {
  customElements.define('pbr-more-to-explore', PbrMoreToExplore);
}
